<template>
    <div class="education-events">
        <div class="tabs" v-if="!invites">
            <div
                class="tab"
                v-for="tab in getTabs"
                :key="tab.key"
                :class="{ active: currentTab === tab.key }"
                @click="tabHandler(tab.key)"
            >{{ tab.label }}</div>
        </div>

        <div class="content" v-loading="isDataLoading">
            <div
                class="empty"
                v-if="!items.length && !isDataLoading"
            >{{ emptyText }}</div>
            <template v-else>

                <div
                    v-if="items.length"
                    class="list"
                >
                    <event-card
                        v-for="item in items"
                        class="event-item"
                        :key="item.id"
                        :item="item"
                        :status="invites ? 'invite' : currentTab"
                        @update="$emit('update')"
                    />
                </div>

                <btn
                    v-if="isLoadMoreShown"
                    class="btn"
                    :disabled="isDataLoading"
                    @click="loadNextPageHandler"
                >
                    <span class="btn-text">Показать еще</span>
                </btn>

            </template>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';

import Btn from '@/components/UI/Button';

import EventCard from './EventCard';

const eventsGetters = [
    'getTypes',
];

const eventsActions = [
    'fetchEvents',
];

export default {
    name: 'EducationEvents',
    components: {
        Btn,
        EventCard,
    },
    props: {
        type: {
            type: String,
            default: '',
        },
        invites: {
            type: String,
            default: '',
        },
    },
    data: () => ({
        isDataLoading: false,
        items: [],
        itemsCount: 0,
        currentPage: 1,
        limit: 4,
        pastEvents: false,
        futureEvents: true,
        participantStatuses: [
            // 'user_sent_request',
            // 'admin_refused_request',
            'admin_confirmed_request',
            'user_confirmed_request',
            'completed_event',
            'missed_event',
        ],
        courseStatusFilters: {
            'active': [
                'admin_confirmed_request',
                'user_confirmed_request',
            ],
            'past': [
                'completed_event',
                'missed_event',
            ],
        },
        currentTab: 'active',
    }),
    computed: {
        tabHasPagination() {
            return this.currentTab !== 'active' && !this.invites;
        },
        isLoadMoreShown() {
            return this.tabHasPagination
                && this.currentPage < this.pagesCount;
        },
        getTabs() {
            let tabs = [];
            
            tabs.push({
                key: 'active',
                label: this.type === 'courses' ? 'Текущие' : 'Активные',
            });

            if (this.type !== 'courses') {
                tabs.push({
                    key: 'future',
                    label: 'Предстоящие',
                });
            }
            
            tabs.push({
                key: 'past',
                label: 'Завершенные',
            });

            return tabs;
        },
        pagesCount() {
            return this.itemsCount ? Math.ceil(this.itemsCount / this.limit) : 0;
        },
        emptyText() {
            if (this.invites) return '';

            switch (this.currentTab) {
                case 'active':
                    return `Сейчас нет активных ${this.getTypes[this.type].pluralGenitive}`;
                case 'future':
                case 'past':
                    return `Вы еще не зарегистрированы ни на ${this.type === 'events' ? 'одно' : 'один'} ${this.getTypes[this.type].name}`;
                default:
                    return '';
            }
        },
        ...mapGetters('events', eventsGetters),
    },
    created() {
        this.reloadData();
    },
    methods: {
        reloadData() {
            if (this.invites) {
                this.loadInvites();
            } else {
                this.tabHandler(this.currentTab, true);
            }
        },
        async loadInvites() {
            this.isDataLoading = true;

            if (this.invites === 'events') {
                const response = await this.fetchEvents({
                    type: 'full_time',
                    status: 'public,private',
                    limit: 9000,
                    offset: 0,
                    self: true,
                    participantStatuses: ['admin_sent_request'],
                });

                this.items = response.events.map(event => ({
                    ...event,
                    timeStatus: 'future',
                    type: 'events',
                }));

                this.isDataLoading = false;
            } else {
                const responseWebinars = await this.fetchEvents({
                    type: 'webinar',
                    status: 'public,private',
                    limit: 9000,
                    offset: 0,
                    self: true,
                    participantStatuses: ['admin_sent_request'],
                });
                const responseCourses = await this.fetchEvents({
                    type: 'course',
                    status: 'public,private',
                    limit: 9000,
                    offset: 0,
                    self: true,
                    participantStatuses: ['admin_sent_request'],
                });

                this.items = [
                    ...responseWebinars.events.map(event => ({
                        ...event,
                        timeStatus: 'future',
                        type: 'webinars',
                    })),
                    ...responseCourses.events.map(event => ({
                        ...event,
                        timeStatus: 'future',
                        type: 'courses',
                    })),
                ];

                this.isDataLoading = false;
            }
        },
        async loadActiveEvents() {
            this.isDataLoading = true;
            this.items = [];
            const responseCurrent = await this.fetchEvents({
                type: this.getTypes[this.type].id,
                status: 'public,private',
                limit: 9000,
                offset: 0,
                self: true,
                currentEvents: this.type !== 'courses',
                participantStatuses: this.participantStatuses.filter(v =>
                    this.type !== 'courses'
                    || this.courseStatusFilters['active'].indexOf(v) > -1
                ),
            });

            let items = responseCurrent.events.map(event => ({
                ...event,
                timeStatus: 'now',
                type: this.type,
            }));

            this.isDataLoading = false;

            if (this.type !== 'courses') {
                this.isDataLoading = true;

                const responseNearest = await this.fetchEvents({
                    type: this.getTypes[this.type].id,
                    status: 'public,private',
                    limit: 9000,
                    offset: 0,
                    self: true,
                    willStartInMinutesEvents: 15,
                    participantStatuses: this.participantStatuses,
                });

                items = [
                    ...items,
                    ...responseNearest.events.map(event => ({
                        ...event,
                        timeStatus: 'begins',
                        type: this.type,
                    })),
                ];

                this.isDataLoading = false;
            }
            this.items = items;

        },
        async loadEvents(paginated, status) {
            this.isDataLoading = true;
            let datetimeBeginFrom = null;
            if (this.futureEvents && this.type !== 'courses') {
                datetimeBeginFrom = moment().add(16, 'minutes').format('YYYY-MM-DD HH:mm');
            }
            const response = await this.fetchEvents({
                type: this.getTypes[this.type].id,
                status: 'public,private',
                limit: this.limit,
                offset: ( this.limit * this.currentPage ) - this.limit,
                self: true,
                pastEvents: this.pastEvents && this.type !== 'courses',
                futureEvents: this.futureEvents && this.type !== 'courses',
                datetimeBeginFrom,
                participantStatuses: this.participantStatuses.filter(v =>
                    this.type !== 'courses'
                    || this.courseStatusFilters[status].indexOf(v) > -1
                ),


            });
            if (paginated) {
                this.items = this.items.concat(response.events);
            } else {
                this.items = response.events;
            }
            this.items.forEach(event => {
                event.timeStatus = status;
                event.type = this.type;
            });
            this.itemsCount = response.count;
            this.isDataLoading = false;
        },
        tabHandler(key, force) {
            if (key === this.currentTab && !force) return;
            this.currentTab = key;
            this.currentPage = this.tabHasPagination ? 1 : null;
            this.fetchTabEvents(false);
        },
        fetchTabEvents(paginated) {
            switch (this.currentTab) {
                case 'active':
                    this.loadActiveEvents();
                    break;
                case 'future':
                    this.pastEvents = false;
                    this.futureEvents = true;
                    this.loadEvents(paginated, 'future');
                    break;
                case 'past':
                    this.pastEvents = true;
                    this.futureEvents = false;
                    this.loadEvents(paginated, 'past');
                    break;
                default:
                    break;
            }
        },
        loadNextPageHandler() {
            this.currentPage += 1;
            this.fetchTabEvents(true);
        },
        ...mapActions('events', eventsActions),
    },
};
</script>

<style lang="scss" scoped>
@import "@/styles/rem";
@import "@/styles/mrem";
@import "@/styles/variables";

.education-events{
    .tabs {
        background-color: #fff;
        height: 2rem;
        display: flex;
        user-select: none;
        cursor: pointer;
        margin-bottom: 1.2rem;
        * {
            font-size: .8rem;
            font-weight: 500;
        }

        @include on-mobile {
            height: auto;
            flex-direction: column;
            align-items: stretch;
            margin-bottom: mrem(32);
        }
        .tab {
            padding: 0 2rem;
            display: flex;
            align-items: center;
            justify-content: center;
            border-bottom: .12rem solid #BAC5CB;

            @include on-mobile {
                padding: mrem(12) 0;
                border-bottom: none;
                background: #FFFFFF;
                border: 1px solid #D2D5D9;
                border-radius: mrem(4);
                font-weight: bold;
                font-size: mrem(14);
                line-height: mrem(16);
                color: #4663AB;
            }
            &.active {
                color: #4663AB;
                border-color: #4663AB;

                @include on-mobile {
                    background: #4663AB;
                    border-color: #4663AB;
                    color: white;
                    
                }
            }
            &:not(:last-child) {
                @include on-mobile {
                    margin-bottom: mrem(4);
                }
            }
        }
    }

    .content{
        position: relative;
        min-height: rem(80);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        .preloader{
            position: absolute;
            z-index: 999;
            background-color: rgba(255, 255, 255, 0.9);
        }

        .empty {
            width: 100%;
            text-align: center;
            color: #98A0A9;
            font-size: rem(15);
            line-height: rem(22);
            font-weight: 600;
            @include on-mobile {
                font-size: mrem(15);
                line-height: mrem(22);
            }
        }
        .list{
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: stretch;
            .event-item {
                margin-bottom: rem(32);
                @include on-mobile {
                    margin-bottom: mrem(40);
                }
                &.invite{
                    margin-bottom: rem(16);
                    @include on-mobile {
                        margin-bottom: mrem(40);
                    }
                }
                &:last-child{
                    margin-bottom: 0;
                }
            }
        }

        .btn{
            margin-top: rem(24);
            @include on-mobile {
                margin-top: mrem(40);
            }
            .btn-text{
                font-weight: bold;
                font-size: rem(16);
                line-height: rem(24);
                @include on-mobile {
                    font-size: mrem(16);
                    line-height: mrem(24);
                }
            }
        }
    }
}

// @media (max-width: $vm) {
//     .tabs {
//         height: auto;
//         background-color: #fff;
//         display: flex;
//         flex-direction: column;
//         margin-bottom: mrem(24);

//         * {
//             font-size: mrem(14);
//         }
//     }

//     .tab {
//         width: 100%;
//         height: mrem(40);
//         padding: 0;
//         margin-bottom: mrem(4);
//         border: mrem(1) solid #D2D5D9;
//         border-radius: mrem(4);
//         color: #3874E0;
//         background-color: #fff;

//         &.active {
//             color: #fff;
//             background-color: #3874E0;
//         }
//     }

//     .empty {
//         font-size: mrem(14);
//         line-height: mrem(17);
//         font-weight: 500;
//         margin: 0;
//     }
// }
</style>